import { _ } from "../../../polyglot/polyglot.js";
import { formatAsMonthAndYear } from "../../../helpers/date-formatting-util.js";
import { makeMonitorRepository } from "../../../repositories/repositoryBuilders.js";
import AbstractImporter from "../AbstractImporter.js";
import DropdownSelectionStep from "../DropdownSelectionStep.js";
import OiIntegration from "../../../../models/integration/OiIntegration.js";
import session from "../../../session.js";

/**
 * `OpenIntegrationImporter` imports from OpenIntegration.
 */
export default class OpenIntegrationImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		const antiForgeryToken = session.getAntiForgeryToken();

		this._oiFiscalYearsRepository = makeMonitorRepository({
			route: "/Shared/Import/OpenIntegration/FiscalYears/#companyId",
			antiForgeryToken,
		});

		this._oiImportRepository = makeMonitorRepository({
			route: "/Shared/Import/OpenIntegration",
			antiForgeryToken,
		});

		this._companyFiscalYearsRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/FiscalYearInfo?onlyActive=true",
			antiForgeryToken,
		});
	}

	_wantChartOfAccountsConfigurationInConfigureImportDataStep() {
		return false;
	}

	_wizardSteps() {
		return [this._selectFiscalYearStep()];
	}

	// step 0: Fetch available Fiscal Years
	_doStart({ onSuccess, onError }) {
		const fetchFiscalYears = this._oiFiscalYearsRepository.get({
			filter: { companyId: this._context.companyId },
		});

		const fetchCompanyFiscalYears = this._companyFiscalYearsRepository.get({
			filter: { companyId: this._context.companyId },
		});

		Promise.all([fetchFiscalYears, fetchCompanyFiscalYears]).then(
			([oiFiscalYears, companyFiscalYears]) => {
				this._context.fiscalYears = oiFiscalYears;
				this._setLastActiveFiscalYear(companyFiscalYears);
				onSuccess();
			},
			(error) => {
				onError({
					errorMessage: this._errorToMessage(error),
				});
			},
		);
	}

	_setLastActiveFiscalYear(fiscalYears) {
		this._context.lastActiveFiscalYear = fiscalYears
			? fiscalYears.sort(
					(a, b) =>
						a.lastActiveTransactionDate -
						b.lastActiveTransactionDate,
				)[0]
			: undefined;
	}

	_selectFiscalYearStep() {
		return new DropdownSelectionStep({
			dropdownLabel: _("Fiscal year"),
			getItemsFromContext: (context) =>
				this._makeAllFiscalYearsDropdownItems(context.fiscalYears),
			submitCallback: this._chosenFiscalYear.bind(this),
		});
	}

	_sortFiscalYears(itemA, itemB) {
		return itemB.label.localeCompare(itemA.label);
	}

	_makeAllFiscalYearsDropdownItems(fiscalYears) {
		const items = this._makeFetchedFiscalYearsDropdownItems(fiscalYears);

		if (items.length === 1) {
			return items;
		}

		return [
			{
				label: _("Add all fiscal years"),
				value: fiscalYears,
			},
			...items,
		];
	}

	_makeFetchedFiscalYearsDropdownItems(fiscalYears) {
		return fiscalYears
			.map((period) => {
				const lastActiveFiscalYear = this._context.lastActiveFiscalYear;

				return {
					label: `${formatAsMonthAndYear(
						period.startDate,
					)}-${formatAsMonthAndYear(period.endDate)}`,
					value: [period],
					selected: lastActiveFiscalYear
						? period.startDate === lastActiveFiscalYear.start
						: false,
				};
			})
			.sort(this._sortFiscalYears.bind(this));
	}

	// Step 1 -> 2: Import
	_chosenFiscalYear({ onDone, step, selection }) {
		this._context.fiscalYear = selection;

		this._oiImportRepository
			.save({
				model: {
					periods: selection,
					companyId: this._context.companyId,
				},
				notify: false,
			})
			.then((data) => {
				this._context.importData = data;
				onDone();
			})
			.catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});
	}

	_errorToMessage(error) {
		switch (error.responseJSON.typeOfError) {
			case "ApiRequestError": {
				return _(
					"At the moment there seems to be problems with the {externalAPI} server. Please try again in a few minutes.",
					{ externalAPI: "OpenIntegration API" },
				);
			}
			case "UnmappedAccountFoundError": {
				return _(
					"You have unmapped accounts. Please map the accounts in {OpenIntegration}.",
					{
						OpenIntegration: (html) =>
							html.a(
								{
									href: "https://onlinedefendersac.accountantspace-dev.co.uk/html/",
								},
								_("Open Integration"),
							),
					},
				);
			}
			case "AccountNotFoundError": {
				return _(
					"Can not find the account with the nominal code '{nominalCode}', please contact support.",
					{
						nominalCode: error.responseJSON.message,
					},
				);
			}
			default: {
				return error.responseJSON.message;
			}
		}
	}

	static loadingMessage() {
		return _("Fetching data from OI…");
	}
}

OpenIntegrationImporter.integrationType = OiIntegration.getType();
